import React from 'react';

import { BuilderBlock, BuilderControls } from './builder';
import Kit from '../layout/Kit.jsx';
import Layout from './Layout.jsx';
import Types from './Types.jsx';
import Url from './Url.jsx';
import Utilities from './Utilities.jsx';

const Block = {
    // STYLE CONFIG
    _mapBorderProperty: (style, propNameBase, propName, propValue, propUnit) => {
        if (propValue == undefined || propValue == '') {
            return;
        }
        if (!style[`${propNameBase}Style`]) {
            style[`${propNameBase}Style`] = 'solid';
        }
        style[propName] = `${propValue}`;

        if (propUnit) {
            style[propName] += propUnit;
        }
    },

    backgroundPosition: (value) => {
        switch (value) {
            case 'bl':
                return 'left bottom';
            case 'bc':
                return 'center bottom';
            case 'br':
                return 'right bottom';
            case 'ml':
                return 'left center';
            case 'mr':
                return 'right center';
            case 'tl':
                return 'left top';
            case 'tc':
                return 'center top';
            case 'tr':
                return 'right top';
        }
        return 'center center';
    },

    backgroundRepeat: (config) => {
        const repeatX = config.backgroundRepeatX == true;
        const repeatY = config.backgroundRepeatY == true;
        if (repeatX && repeatY) {
            return 'repeat';
        } else if (repeatX) {
            return 'repeat-x';
        } else if (repeatY) {
            return 'repeat-y';
        } else {
            return 'no-repeat'
        }
    },

    findBlockConfig: (onCompare, blocks, parent) => {
        if (!blocks) {
            return null;
        }
        let temp;
        for (let i = 0; i < blocks.length; i++) {
            temp = blocks[i];
            if (!temp) {
                continue;
            }
            if (onCompare(temp)) {
                return { index: i, blocks, parent };
            }
            if (temp.blocks) {
                for (let j = 0; j < temp.blocks.length; j++) {
                    // Check each block in column
                    const blockConfig = Block.findBlockConfig(onCompare, temp.blocks[j], temp);
                    if (!blockConfig) {
                        continue;
                    }
                    return blockConfig;
                }
            }
        }
        return null;
    },

    getStyleConfigPropValue: (propName, configProps) => {
        let prop = configProps[propName];
        if (prop != undefined && prop.unit) {
            const propValue = (prop.value || '').trim();
            if (propValue != "") {
                return `${propValue}${prop.unit}`;
            }
        } else {
            prop = (prop || '').trim();
            if (prop != "") {
                return prop;
            }
        }
        return null;
    },

    mapLayoutStyleConfigPropsWithPageKit: (style, componentProps, keyOverrides, handleProp) => {
        if (!componentProps || componentProps.legacy) {
            return { style };
        }
        const configKey = keyOverrides && keyOverrides.configKey ? keyOverrides.configKey : 'layout';
        let _style = style || {};
        let margin = '0 auto';
        let maxHeight, maxWidth, minWidth, width;
        if (componentProps.blockConfig && componentProps.blockConfig[configKey]) {
            const alignmentKey = keyOverrides && keyOverrides.alignment ? keyOverrides.alignment : 'alignment';
            Block.mapStyleConfigProps(_style, componentProps.blockConfig[configKey], (propName, propValue, propUnit) => {
                if (handleProp && handleProp(propName, propValue, propUnit)) {
                    return true;
                }
                switch (propName) {
                    case 'maxHeight':
                        if (propValue && propUnit) {
                            if (propUnit == '%' && !componentProps.inLayout) {
                                maxHeight = `${propValue}vh`;
                            } else {
                                maxHeight = `${propValue}${propUnit}`;
                            }
                        }
                        return !componentProps.inCell;
                    case 'minHeight':
                        if (propValue && propUnit == '%') {
                            _style.minHeight = `${propValue}vh`;
                            return !componentProps.inCell;
                        }
                        break;
                    case 'maxWidth':
                        if (propValue && propUnit) {
                            maxWidth = `${propValue}${propUnit}`;
                        }
                        return true;
                    case 'minWidth':
                        if (componentProps.inLayout && propValue && propUnit) {
                            minWidth = `${propValue}${propUnit}`;
                        }
                        return true;
                    case 'width':
                        if (propValue && propUnit) {
                            width = `${propValue}${propUnit}`;
                        }
                        return true;
                    case alignmentKey:
                        if (!componentProps.inLayout) {
                            margin = Block.marginForAlignment(propValue);
                        }
                        return true;
                }
            });
        }
        if (componentProps.renderHeaderFooter != false && !componentProps.inCell && !componentProps.inLayout) {
            _style = Kit.Page.updateComponentStyle(_style, componentProps.config, componentProps.pageType);
            if (!maxWidth) {
                maxWidth = Kit.Page.maxWidth(componentProps.config, componentProps.pageType);
            }
        } else if (componentProps.inLayout) {
            margin = undefined;
        }
        let containerStyle;
        if (maxWidth || minWidth || width) {
            if (componentProps.builder && (componentProps.inLayout || componentProps.inSlide)) {
                containerStyle = { width: '100%' };
            } else {
                containerStyle = { margin, maxWidth, minWidth, width };
            }
        }
        if (maxHeight) {
            if (componentProps.builder && componentProps.inLayout) {
                if (!containerStyle) {
                    containerStyle = {};
                }
                containerStyle.height = '100%';
            } else {
                style.maxHeight = maxHeight;
                style.overflow = 'hidden';
            }
            // if (!containerStyle) {
            //     containerStyle = {};
            // }
            // if (componentProps.builder && componentProps.inLayout) {
            //     containerStyle.height = '100%';
            // } else {
            //     containerStyle.maxHeight = maxHeight;
            //     containerStyle.overflow = 'hidden';
            // }
        }
        return { containerStyle, style: _style };
    },

    mapStyleConfigProps: (style, configProps, handleProp) => {
        for (let propName in configProps) {
            let prop = configProps[propName];
            if (prop != undefined && prop.unit) {
                const propValue = (prop.value || '').trim();
                switch (propName) {
                    case 'borderBottomWidth':
                        Block._mapBorderProperty(style, 'borderBottom', propName, propValue, prop.unit);
                        continue;
                    case 'borderLeftWidth':
                        Block._mapBorderProperty(style, 'borderLeft', propName, propValue, prop.unit);
                        continue;
                    case 'borderRightWidth':
                        Block._mapBorderProperty(style, 'borderRight', propName, propValue, prop.unit);
                        continue;
                    case 'borderTopWidth':
                        Block._mapBorderProperty(style, 'borderTop', propName, propValue, prop.unit);
                        continue;
                    default:
                        if (handleProp && handleProp(propName, propValue, prop.unit)) {
                            continue;
                        }
                        break;
                }
                if (propValue != "") {
                    style[propName] = `${propValue}${prop.unit}`;
                }
                continue;
            }
            if (prop != true && prop != false) {
                if (prop && prop.toString) {
                    prop = prop.toString().trim();
                } else {
                    prop = (prop || '').trim();
                }
            }
            switch (propName) {
                case 'borderBottomColor':
                    Block._mapBorderProperty(style, 'borderBottom', propName, prop);
                    continue;
                case 'borderLeftColor':
                    Block._mapBorderProperty(style, 'borderLeft', propName, prop);
                    continue;
                case 'borderRightColor':
                    Block._mapBorderProperty(style, 'borderRight', propName, prop);
                    continue;
                case 'borderTopColor':
                    Block._mapBorderProperty(style, 'borderTop', propName, prop);
                    continue;
                default:
                    if (handleProp && handleProp(propName, prop)) {
                        continue;
                    }
                    break;
            }
            if (prop != "") {
                style[propName] = prop;
            }
        }
    },

    marginForAlignment: (alignment) => {
        switch (alignment) {
            case 'flex-start':
            case 'left':
                return '0 auto 0 0';
            case 'flex-end':
            case 'right':
                return '0 0 0 auto';
            case 'center':
            default:
                return '0 auto';
        }
    },

    updateAlignment: (style, alignment) => {
        if (!style || (!style.maxWidth && !style.width)) {
            return style;
        }
        const _style = {};
        Utilities.copyObject(style, _style);
        switch (alignment) {
            case 'left':
                _style.marginLeft = '0';
                _style.marginRight = 'auto';
                break;
            case 'right':
                _style.marginLeft = 'auto';
                _style.marginRight = '0';
                break;
            case 'center':
            default:
                _style.marginLeft = 'auto';
                _style.marginRight = 'auto';
                break;
        }
        return _style;
    }
};

function withBlockRendering(WrappedComponent) {
    return class extends React.Component {
        constructor(props) {
            super(props);

            this.handleBlockRendering = this.handleBlockRendering.bind(this);
        }

        handleBlockRendering(basePageType, blocks, blockPropsHandler, builderBlockPropsHandler, providedData) {
            const renderedBlocks = [];
            if (!blocks) {
                return renderedBlocks
            }
            const props = this.props;
            blocks.forEach((d, i) => {
                let componentData = Types.Block.getComponentAndData(d, basePageType, providedData || props.data, props.config);
                if (!componentData || !componentData.component) {
                    return;
                }
                let blockProps = {};
                if (props.onBlockProps) {
                    blockProps = props.onBlockProps(d) || {};
                    if (blockProps.block) {
                        componentData = Types.Block.getComponentAndData(blockProps.block, basePageType, providedData || props.data, props.config);
                    }
                }
                let additionalProps = {};
                if (blockPropsHandler) {
                    additionalProps = blockPropsHandler(d, props) || {};
                }
                const blockConfig = blockProps.block ? blockProps.block.config : d.config;
                let builderBlockProps = {};
                if (props.builder && builderBlockPropsHandler) {
                    builderBlockProps = builderBlockPropsHandler(blockProps.block || d, blockConfig, props) || {};
                }
                const id = d.id || `${i}`;
                let block = (
                    <componentData.component
                        key={id}
                        active={blockProps.active}
                        animation={props.animation}
                        baseUrl={props.baseUrl}
                        blockConfig={blockConfig}
                        blockHandler={blockPropsHandler}
                        blockId={id}
                        blocks={blockProps.block ? blockProps.block.blocks : d.blocks}
                        blockType={d.type && d.type.value}
                        builder={props.builder}
                        builderBlockProps={builderBlockProps}
                        builderBlockPropsHandler={builderBlockPropsHandler}
                        config={props.config}
                        context={componentData.context || d.context}
                        data={componentData.data}
                        dataType={d.dataType}
                        disabled={props.disabled}
                        dragging={props.dragging}
                        editor={props.editor}
                        frameDoc={props.frameDoc}
                        handlerUrl={props.handlerUrl}
                        imageSize={props.imageSize}
                        inCell={props.inCell}
                        inLayout={props.inLayout}
                        inSlide={props.inSlide}
                        jsonLD={d.jsonLD || componentData.jsonLD}
                        layoutContainerTabId={this.props.layoutContainerTabId}
                        legacy={this.props.legacy}
                        pageConfig={props.pageConfig}
                        pageType={props.pageType}
                        parentStyle={props.parentStyle}
                        queryDataSub={props.queryDataSub}
                        queryLayout={props.queryLayout}
                        querySub={props.querySub}
                        screenSize={props.screenSize}
                        type={props.type}
                        onBlockClick={props.onBlockClick}
                        onBlockMove={props.onBlockMove}
                        onBlockProps={props.onBlockProps}
                        onDragEnter={props.onDragEnter}
                        onDragStart={props.onDragStart}
                        onLayoutContainerTabSelection={props.onLayoutContainerTabSelection}
                        {...additionalProps} />
                );
                if (props.builder && !props.disabled) {
                    switch (d.dataType) {
                        case 'layout':
                        case 'layout-horizontal':
                        case 'layout-spacer':
                        case 'layout-vertical':
                            break;
                        default:
                            block = (
                                <BuilderBlock
                                    key={id}
                                    active={blockProps.active}
                                    blockId={id}
                                    blockType={d.type && d.type.value}
                                    dataType={d.dataType}
                                    dragging={props.dragging}
                                    onBlockClick={props.onBlockClick}
                                    onBlockMove={props.onBlockMove}
                                    onDragEnter={props.onDragEnter}
                                    onDragStart={props.onDragStart}
                                    {...builderBlockProps}>
                                    {
                                        builderBlockProps.renderControls != false &&
                                        <BuilderControls {...props} />
                                    }
                                    {block}
                                </BuilderBlock>
                            );
                            break;
                    }
                }
                renderedBlocks.push(block);
            });
            return renderedBlocks;
        }

        render() {
            return (
                <WrappedComponent
                    {...this.props}
                    onBlockRendering={this.handleBlockRendering}>
                    {this.props.children}
                </WrappedComponent>
            );
        }
    }
}

function withConfig(WrappedComponent) {
    return class extends React.Component {
        constructor(props) {
            super(props);

            const blockIdentifier = Utilities.generateUniqueId();
            this.state = {
                blockIdentifier: blockIdentifier,
                q: props.data ? {} : undefined,
            };
        }

        handleNormalizeBlockConfig(props) {
            let blockConfig = props.blockConfig || {};
            if (blockConfig.block) {
                blockConfig = blockConfig.block;
            }
            return blockConfig;
        }

        render() {
            return (
                <WrappedComponent
                    active={this.props.active}
                    backgroundColor={this.props.backgroundColor}
                    baseUrl={this.props.baseUrl}
                    blockConfig={this.props.blockConfig}
                    blockId={this.props.blockId}
                    blockIdentifier={this.state.blockIdentifier}
                    blockType={this.props.blockType}
                    builder={this.props.builder}
                    config={this.props.config}
                    context={this.props.context}
                    data={this.props.data}
                    disabled={this.props.disabled}
                    frameDoc={this.props.frameDoc}
                    handlerUrl={this.props.handlerUrl}
                    imageSize={this.props.imageSize}
                    inCell={this.props.inCell}
                    inLayout={this.props.inLayout}
                    jsonLD={this.props.jsonLD}
                    layoutContainerTabId={this.props.layoutContainerTabId}
                    legacy={this.props.legacy}
                    legacyData={this.props.legacyData}
                    pageConfig={this.props.pageConfig}
                    pageType={this.props.pageType}
                    q={this.state.q}
                    queryDataSub={this.props.queryDataSub}
                    queryLayout={this.props.queryLayout}
                    querySub={this.props.querySub}
                    screenSize={this.props.screenSize}
                    onAsyncLoad={this.props.onAsyncLoad}
                    onBlankState={this.props.onBlankState}
                    onBlockMove={this.props.onBlockMove}
                    onLayoutContainerTabSelection={this.props.onLayoutContainerTabSelection}
                    onNormalizeBlockConfig={this.handleNormalizeBlockConfig}>
                    {this.props.children}
                </WrappedComponent>
            );
        }
    }
}

function withGridConfig(WrappedComponent, defaultColumnCount, keyOverrides) {
    return function (props) {
        let style = props.style || {};
        let columnCount, columnSpacing, lineSpacing, useTokenization;
        if (props.blockConfig) {
            if (props.blockConfig.block) {
                columnSpacing = props.blockConfig.block.columnSpacing;

                let cellKey = 'cell';
                if (keyOverrides && keyOverrides.cell) {
                    cellKey = keyOverrides.cell;
                }
                if (props.blockConfig.block[cellKey] && props.blockConfig.block[cellKey].desktop && props.blockConfig.block[cellKey].desktop.blocks) {
                    useTokenization = props.blockConfig.block[cellKey].desktop.blocks.length > 0;
                }
                let columnsKey = 'columns';
                if (keyOverrides && keyOverrides.columns) {
                    columnsKey = keyOverrides.columns;
                }
                if (props.blockConfig.block[columnsKey]) {
                    columnCount = parseInt(props.blockConfig.block[columnsKey], 10);
                }
                if (props.blockConfig.block.lineSpacing && props.blockConfig.block.lineSpacing.unit != undefined) {
                    const value = props.blockConfig.block.lineSpacing.value || 0;
                    lineSpacing = `${value}${props.blockConfig.block.lineSpacing.unit}`;
                }
            } else {
                columnCount = props.blockConfig.columns;
            }
        }
        let alignmentKey = 'textAlign';
        if (keyOverrides && keyOverrides.alignment) {
            alignmentKey = keyOverrides.alignment;
        }
        const layoutStyles = Block.mapLayoutStyleConfigPropsWithPageKit(style, props, { alignment: alignmentKey });
        if (props.inLayout && layoutStyles.containerStyle && !layoutStyles.containerStyle.width && (layoutStyles.containerStyle.maxWidth || layoutStyles.containerStyle.minWidth)) {
            layoutStyles.containerStyle.width = '100%';
        }
        if (columnCount == undefined || isNaN(columnCount)) {
            columnCount = defaultColumnCount;
        }
        const columnSize = Layout.columnSize(columnCount);
        return (
            <WrappedComponent
                {...props}
                columnCount={columnCount}
                columnSize={columnSize}
                columnSpacing={columnSpacing}
                containerStyle={layoutStyles.containerStyle}
                lineSpacing={lineSpacing}
                style={layoutStyles.style}
                useTokenization={useTokenization}>
                {props.children}
            </WrappedComponent>
        )
    }
}

function withMediaQueryScreenSize(WrappedComponent) {
    return function (props) {
        const [screenSize, setScreenSize] = React.useState(Types.ScreenSize.Desktop);
        const screenSizeRef = React.useRef();
        screenSizeRef.current = screenSize;
        const updateScreenSize = () => {
            if (props.builder) {
                // console.log(`screen size: ${props.displayMode}`);
                setScreenSize(props.displayMode);
                return;
            }
            if (!window || !window.getComputedStyle || !document || !document.body) {
                return;
            }
            const value = window.getComputedStyle(document.body).getPropertyValue('--mobile-screen-size');
            if (value != screenSize.current) {
                setScreenSize(value);
            }
            // console.log(`screen size: ${value}`);
        };
        React.useEffect(() => {
            updateScreenSize();
            if (window) {
                window.addEventListener('resize', updateScreenSize);
                return () => window.removeEventListener('resize', updateScreenSize);
            }
        }, []);
        if (props.builder) {
            React.useEffect(updateScreenSize, [props.displayMode]);
        }
        return <WrappedComponent screenSize={screenSize} {...props}>{props.children}</WrappedComponent>
    }
}

function JsonLD(props) {
    if (props.builder || !props.jsonLD || props.jsonLD.length == 0) {
        return null;
    }
    const itemListElements = props.jsonLD.map((d, i) => {
        const url = Url.getLink(props.baseUrl, `${props.path}/${d.id}`, { sub: props.querySub, dataSub: props.dataSub });
        return { "position": i + 1, "@type": "ListItem", "url": url };
    })
    let jsonContent = { "@context": "https://schema.org", "@type": "ItemList", "itemListElement": itemListElements };
    jsonContent = JSON.stringify(jsonContent);
    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonContent }}></script>;
}

export {
    Block,
    JsonLD,
    withBlockRendering,
    withConfig,
    withGridConfig,
    withMediaQueryScreenSize
};